/**
 * IrrigationPlanHeader component
 * Displays the header section of the irrigation plan page
 */

import { BarChart3 } from "lucide-react";
import Button from "@/components/ui/Button";
import type { IrrigationPlanStepFormData } from "@/pages/main/IrrigationPlanWizardPage";

// Use any to avoid type mismatches with AUT types
type IrrigationPlan = any;
type Project = any;

interface IrrigationPlanHeaderProps {
  /** The current plan (null in create mode) */
  plan: IrrigationPlan | null;
  /** Whether this is create mode */
  isCreateMode: boolean;
  /** Current plan steps */
  planSteps: IrrigationPlanStepFormData[];
  /** Total duration in seconds */
  totalDuration: number;
  /** The project this plan belongs to */
  project: Project | null;
  /** Handler for viewing execution report */
  onViewExecutionReport: () => void;
}

/**
 * Formats duration in seconds to a readable string
 */
function formatDuration(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  return `${minutes} min`;
}

/**
 * Gets status text for enabled state
 */
function getStatusText(isEnabled: boolean): string {
  return isEnabled ? "Ativo" : "Inativo";
}

/**
 * Gets status color class for enabled state
 */
function getStatusColor(isEnabled: boolean): string {
  return isEnabled ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700";
}

export function IrrigationPlanHeader({
  plan,
  isCreateMode,
  planSteps,
  totalDuration,
  project,
  onViewExecutionReport,
}: IrrigationPlanHeaderProps) {
  return (
    <div className="bg-white border-b border-gray-200 px-4 py-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div>
            <h1 className="text-lg font-semibold text-gray-900">{isCreateMode ? "Novo Cronograma" : plan?.name}</h1>
            <div className="flex items-center gap-3 text-sm text-gray-600">
              {!isCreateMode && plan && (
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(plan.is_enabled)}`}>
                  {getStatusText(plan.is_enabled)}
                </span>
              )}
              <span>{planSteps.length} setores</span>
              <span>{formatDuration(totalDuration)}</span>
              {isCreateMode && project && <span className="font-medium">Projeto: {project.name}</span>}
            </div>
          </div>
        </div>
        {!isCreateMode && plan && (
          <div className="flex items-center gap-2">
            <Button
              onClick={onViewExecutionReport}
              variant="secondary"
              size="sm"
              icon={<BarChart3 className="h-4 w-4" />}
              iconPosition="left"
            >
              Relatório
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
